import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Award,
  TrendingUp,
  AlertTriangle,
  CreditCard,
  CheckCircle,
  XCircle,
  Download,
  FileText
} from 'lucide-react';
import { getCustomerPerformanceReport } from '../../../api/wholesale/reports';
import { getPriceTiers } from '../../../api/wholesale/pricing';
import { exportCustomerPerformanceReport } from '../../../api/wholesale/exports';
import { useFiscalPeriod } from '../../../contexts/FiscalPeriodContext';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const CustomerPerformanceReportPage = () => {
  const { periodRange, getDateRangeParams } = useFiscalPeriod();
  const [reportData, setReportData] = useState(null);

  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    tier_id: '',
    min_revenue: ''
  });

  const fetchTiers = async () => {
    try {
      const response = await getPriceTiers();
      const data = response?.data || response || [];
      setTiers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching tiers:', err);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};

      // Use custom dates if provided, otherwise use fiscal period
      if (filters.date_from || filters.date_to) {
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
      } else {
        const dateParams = getDateRangeParams();
        if (dateParams.start_date) params.date_from = dateParams.start_date;
        if (dateParams.end_date) params.date_to = dateParams.end_date;
        if (dateParams.ignore_global_period) params.ignore_global_period = true;
      }

      if (filters.tier_id) params.tier_id = filters.tier_id;
      if (filters.min_revenue) params.min_revenue = filters.min_revenue;

      const response = await getCustomerPerformanceReport(params);
      setReportData(response?.data || response);
    } catch (err) {
      console.error('Error fetching customer performance report:', err);
      setError('Failed to load customer performance report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiers();
    fetchReport();
  }, [periodRange]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const handleExport = async (format) => {
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.tier_id) params.tier_id = filters.tier_id;
      if (filters.min_revenue) params.min_revenue = filters.min_revenue;

      const response = await exportCustomerPerformanceReport(params, format);

      if (response.success && response.data?.file_url) {
        window.open(response.data.file_url, '_blank');
        alert(`Customer performance report exported successfully as ${format.toUpperCase()}`);
      }
    } catch (err) {
      console.error('Error exporting report:', err);
      alert(err.message || 'Failed to export report');
    }
  };

  const handleResetFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      tier_id: '',
      min_revenue: ''
    });
    setTimeout(() => fetchReport(), 100);
  };

  if (loading && !reportData) {
    return <LoadingSpinner />;
  }

  const topCustomers = reportData?.top_customers || [];
  const tierDistribution = reportData?.tier_distribution || [];
  const paymentBehavior = reportData?.payment_behavior || {};
  const creditSummary = reportData?.credit_summary || {};

  // Calculate payment behavior percentages
  const totalOrders = parseInt(paymentBehavior.total_orders || 0);
  const paidOnTimePercent =
    totalOrders > 0 ? (parseInt(paymentBehavior.paid_on_time || 0) / totalOrders) * 100 : 0;
  const overduePercent =
    totalOrders > 0 ? (parseInt(paymentBehavior.overdue || 0) / totalOrders) * 100 : 0;

  const tierColorClasses = {
    1: 'bg-amber-600',
    2: 'bg-gray-400',
    3: 'bg-yellow-500',
    4: 'bg-purple-600'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Customer Performance Report</h1>
        <p className="text-gray-500 mt-1">Analyze distributor behavior and performance metrics</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
            <select
              value={filters.tier_id}
              onChange={(e) => handleFilterChange('tier_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">All Tiers</option>
              {tiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.tier_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Revenue (₦)</label>
            <input
              type="number"
              value={filters.min_revenue}
              onChange={(e) => handleFilterChange('min_revenue', e.target.value)}
              placeholder="e.g., 1000000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Apply Filters
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Reset
          </button>

          {/* Export Buttons */}
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => handleExport('csv')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              disabled={loading}
            >
              <FileText className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('excel')}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
              disabled={loading}
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Total Distributors</p>
            <Users className="h-5 w-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {creditSummary.total_distributors || 0}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Credit Extended</p>
            <CreditCard className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ₦{parseFloat(creditSummary.total_credit_extended || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Avg Utilization</p>
            <TrendingUp className="h-5 w-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {parseFloat(creditSummary.avg_utilization_percent || 0).toFixed(1)}%
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-500">Credit Warnings</p>
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="space-y-1">
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-red-600">
                {creditSummary.exceeded_limit || 0}
              </span>{' '}
              Exceeded
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-yellow-600">{creditSummary.near_limit || 0}</span>{' '}
              Near Limit
            </p>
          </div>
        </div>
      </div>

      {/* Payment Behavior */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Payment Behavior Analysis</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">Paid On Time</p>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-600">{paidOnTimePercent.toFixed(1)}%</p>
            <p className="text-xs text-gray-500 mt-1">
              {paymentBehavior.paid_on_time || 0} of {totalOrders} orders
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">Overdue</p>
              <XCircle className="h-5 w-5 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-600">{overduePercent.toFixed(1)}%</p>
            <p className="text-xs text-gray-500 mt-1">
              {paymentBehavior.overdue || 0} of {totalOrders} orders
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-500">Avg Days to Payment</p>
              <Award className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {parseFloat(paymentBehavior.avg_days_to_payment || 0).toFixed(0)} days
            </p>
          </div>
        </div>
      </div>

      {/* Tier Distribution */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Distribution by Tier</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {tierDistribution.map((tier) => {
            const colorClass = tierColorClasses[tier.tier_level] || 'bg-gray-500';
            return (
              <div key={tier.tier_level} className={`${colorClass} text-white rounded-lg p-4`}>
                <div className="flex items-center gap-2 mb-2">
                  <Award className="h-5 w-5" />
                  <p className="font-bold">{tier.tier_name}</p>
                </div>
                <p className="text-2xl font-bold mb-1">{tier.distributor_count || 0}</p>
                <p className="text-sm opacity-90">Distributors</p>
                <div className="mt-3 pt-3 border-t border-white/30">
                  <p className="text-xs opacity-90">{tier.total_orders || 0} orders</p>
                  <p className="text-xs opacity-90">
                    ₦{parseFloat(tier.total_revenue || 0).toLocaleString()} revenue
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tier Distribution Pie Chart */}
        {tierDistribution.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Distributor Distribution Visualization
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={tierDistribution}
                  dataKey="distributor_count"
                  nameKey="tier_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ tier_name, percent }) => `${tier_name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {tierDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={['#d97706', '#9ca3af', '#eab308', '#9333ea'][index % 4]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => [
                    `${value} distributors (₦${parseFloat(props.payload.total_revenue || 0).toLocaleString()} revenue)`,
                    props.payload.tier_name
                  ]}
                  contentStyle={{ fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Top Customers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Top Customers by Revenue</h2>

        {/* Top Customers Bar Chart */}
        {topCustomers.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Top 10 Customers Revenue Comparison
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topCustomers.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="business_name"
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={120}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => `₦${parseFloat(value).toLocaleString()}`}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend />
                <Bar dataKey="total_revenue" fill="#10b981" name="Revenue" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Distributor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Orders
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Credit Util.
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Payment Rate
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {topCustomers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No data available
                  </td>
                </tr>
              ) : (
                topCustomers.map((customer, idx) => {
                  const utilization = parseFloat(customer.credit_utilization_percent || 0);
                  const paymentRate =
                    customer.total_orders > 0
                      ? (parseInt(customer.paid_orders || 0) / parseInt(customer.total_orders)) *
                        100
                      : 0;

                  return (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-bold text-gray-500">#{idx + 1}</td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/wholesale/distributors/${customer.id}`}
                          className="text-sm font-medium text-emerald-600 hover:text-emerald-800"
                        >
                          {customer.business_name}
                        </Link>
                        <div className="text-sm text-gray-500">{customer.distributor_number}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{customer.tier_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{customer.total_orders}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-green-600">
                        ₦{parseFloat(customer.total_revenue || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                            <div
                              className={`h-2 rounded-full ${utilization > 80 ? 'bg-red-500' : utilization > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(utilization, 100)}%` }}
                            />
                          </div>
                          <span
                            className={`text-xs font-medium ${utilization > 80 ? 'text-red-600' : utilization > 60 ? 'text-yellow-600' : 'text-gray-700'}`}
                          >
                            {utilization.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`text-sm font-medium ${paymentRate >= 80 ? 'text-green-600' : paymentRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}
                        >
                          {paymentRate.toFixed(0)}%
                        </span>
                        <div className="text-xs text-gray-500">
                          {customer.paid_orders}/{customer.total_orders} paid
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomerPerformanceReportPage;
